// ============================================================
// civilDate.ts — THE shared civil-date helpers (frontend). Tested in
// civilDate.test.ts. Mirrors backend/src/lib/civilDate.ts — keep in step.
//
//   • londonToday()      — "today" in Europe/London as YYYY-MM-DD. Use this,
//                          never `new Date().toISOString().slice(0, 10)` (UTC:
//                          between 00:00 and 01:00 in British Summer Time it's
//                          still YESTERDAY).
//   • londonDayOf(d)     — the Europe/London civil day an INSTANT falls on.
//   • isRealIsoDate(s)   — strict "YYYY-MM-DD" that exists (no 2023-02-30).
//   • textDateToIso(raw) — "YYYY-MM-DD", an ISO timestamp or UK "D/M/YYYY"
//                          → "YYYY-MM-DD" or null (the free-text date columns).
//   • addYearsIso(iso,n) — same day n years on; 29 Feb → 28 Feb (earlier, safe).
//   • ukDate(iso)        — "dd/mm/yyyy" for messages.
//   • yearsBetween(a,b)  — whole years from a to b (age on b).
// ============================================================
const LONDON_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' })

export const londonDayOf = (instant: Date): string => LONDON_DAY.format(instant)
export const londonToday = (now: Date = new Date()): string => londonDayOf(now)

export const isRealIsoDate = (s: unknown): s is string => {
  if (typeof s !== 'string') return false
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return false
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3])
  if (y < 1000) return false
  const dt = new Date(Date.UTC(y, mo - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d
}

export const textDateToIso = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null
  const s = raw.trim()
  const uk = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s)
  if (uk) {
    const iso = `${uk[3]}-${uk[2].padStart(2, '0')}-${uk[1].padStart(2, '0')}`
    return isRealIsoDate(iso) ? iso : null
  }
  const head = /^(\d{4}-\d{2}-\d{2})(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/.exec(s)
  if (head && isRealIsoDate(head[1])) return head[1]
  return null
}

export const addYearsIso = (iso: string, years: number): string | null => {
  if (!isRealIsoDate(iso)) return null
  const [y0, mo, d0] = iso.split('-').map(Number)
  const y = y0 + years
  const dim = new Date(Date.UTC(y, mo, 0)).getUTCDate()
  const d = Math.min(d0, dim)
  return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export const ukDate = (iso: string): string => { const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}/${m}/${y}` }

export const yearsBetween = (fromIso: string, onIso: string): number => {
  const [fy, fm, fd] = fromIso.split('-').map(Number)
  const [oy, om, od] = onIso.split('-').map(Number)
  let n = oy - fy
  if (om < fm || (om === fm && od < fd)) n--
  return n
}
